import { useEffect, useState } from "react";
import clsx from "clsx";
import { InlinePdfView } from "@/components/pdf/InlinePdfView";
import { Spinner } from "@/components/ui/Spinner";
import { fetchBlob } from "@/features/timesheetFlow/timesheetFlowApi";

export function timesheetPdfApiPath(timesheetId: number, isSigned: boolean): string {
  return isSigned ? `/timesheets/${timesheetId}/pdf/latest` : `/timesheets/${timesheetId}/pdf/generate`;
}

export function timesheetAttachmentPdfApiPath(timesheetId: number, attachmentId: number): string {
  return `/timesheets/${timesheetId}/attachments/${attachmentId}`;
}

/** Inline-PDF-Vorschau für die Detail-Übersicht. */
export function TimesheetPdfEmbed({
  timesheetId,
  isSigned,
  apiPath,
  title = "Stundennachweis PDF",
  className,
  bare = false,
}: {
  timesheetId: number;
  isSigned: boolean;
  /** Optionaler API-Pfad; überschreibt die Standard-Auswahl signed/generate. */
  apiPath?: string;
  title?: string;
  className?: string;
  /** Ohne Rahmen und Kopfzeile – maximale Fläche für das PDF. */
  bare?: boolean;
}) {
  const pdfPath = apiPath ?? timesheetPdfApiPath(timesheetId, isSigned);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setBlob(null);
    void fetchBlob(pdfPath)
      .then((next) => {
        if (!cancelled) setBlob(next);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "PDF konnte nicht geladen werden.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [pdfPath]);

  if (bare) {
    return (
      <div
        className={clsx(
          "flex min-h-[min(58vh,32rem)] flex-1 flex-col bg-fill-quaternary/15",
          className,
        )}
      >
        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <Spinner />
          </div>
        ) : null}
        {error ? (
          <p className="flex flex-1 items-center justify-center px-4 text-center text-sm text-status-red">
            {error}
          </p>
        ) : null}
        {blob && !loading && !error ? (
          <InlinePdfView blob={blob} title={title} className="min-h-0 w-full flex-1" />
        ) : null}
      </div>
    );
  }

  return (
    <section
      className={clsx(
        "flex min-h-[280px] flex-col overflow-hidden rounded-ios-lg border border-separator bg-fill-quaternary/20",
        className,
      )}
    >
      <div className="relative min-h-[240px] flex-1 bg-card">
        {loading ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <Spinner />
          </div>
        ) : null}
        {error ? (
          <p className="absolute inset-0 flex items-center justify-center px-4 text-center text-sm text-status-red">
            {error}
          </p>
        ) : null}
        {blob ? (
          <InlinePdfView blob={blob} title={title} className="absolute inset-0 h-full w-full" />
        ) : null}
      </div>
    </section>
  );
}
